/**
 * mod-cart-event-bus.ts
 *
 * Fonte da verdade para mudanças no carrinho.
 *
 * Escutando mudanças (em qualquer arquivo .ts):
 *   import { INSIDER_CART_CHANGED } from './mod-cart-event-bus.js';
 *   import type { CartChangedDetail } from './mod-cart-event-bus.js';
 *   document.addEventListener(INSIDER_CART_CHANGED, (e: CustomEvent<CartChangedDetail>) => {
 *     const { source, operation, timestamp, cart } = e.detail;
 *   });
 *
 * Escutando em arquivos .js legados:
 *   document.addEventListener('insider:cart:changed', (e) => {
 *     const { source, operation, cart } = e.detail;
 *   });
 *
 * Disparando em arquivos .ts:
 *   import { dispatch, fetchAndDispatch } from './mod-cart-event-bus.js';
 *   dispatch({ source: 'meu-componente', operation: 'update', cart });
 *   await fetchAndDispatch('meu-componente', 'add');
 *
 * Disparando em arquivos .js legados (helpers globais):
 *   dispatchCartEvent({ source: 'meu-componente', operation: 'update', cart });
 *   fetchCartAndDispatch('meu-componente', 'add');
 *
 * ─── Detecção de apps externos ────────────────────────────────────────────────
 * Duas camadas rodam em paralelo para detectar POSTs de apps terceiros
 * (freegifts, Stamped, bundles) que não passam pelos helpers acima:
 *
 *   1. fetch/XHR monkey-patch (preferencial, ~0ms, lê body da resposta direto)
 *   2. PerformanceObserver (fallback, ~50-200ms + janela de supressão de 2s)
 *
 * Dedup por fingerprint no dispatch() garante zero evento duplicado quando
 * ambas detectam a mesma mutação.
 *
 * Kill switch em produção (sem deploy):
 *   No console do browser:  window.__cartBusDisableFetchPatch = true
 *   Depois F5. A camada 1 não instala; Observer continua ativo.
 *   Para reverter: remove a variável ou seta false, F5.
 */

// ─── Constantes públicas ──────────────────────────────────────────────────────

export const INSIDER_CART_CHANGED = "insider:cart:changed" as const;
export const INSIDER_CART_ERROR = "insider:cart:error" as const;

// ─── Tipos exportados ─────────────────────────────────────────────────────────

export type CartChangedSource =
  | "product-form"
  | "quick-add"
  | "cart-items"
  | "kit-variant-selector"
  | "cart-limits"
  | "minicart-coupon"
  | "cart-note"
  | "gift-card-form"
  | "external"
  | "unknown";

export type CartOperation = "add" | "remove" | "update" | "update-note" | "update-discount";

export interface ShopifyCartItem {
  id: number;
  title: string;
  price: number;
  line_price: number;
  quantity: number;
  sku: string;
  grams: number;
  vendor: string;
  product_id: number;
  variant_id: number;
  handle: string;
  url: string;
  image: string;
  properties: Record<string, string> | null;
  product_type: string;
  variant_title: string | null;
}

export interface ShopifyCart {
  token: string;
  note: string | null;
  attributes: Record<string, string>;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: ShopifyCartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: unknown[];
  discount_codes: Array<{ code: string; applicable: boolean }>;
}

export interface CartChangedDetail {
  /** De onde veio a mutação */
  source: CartChangedSource;
  /** Tipo de operação realizada no carrinho */
  operation: CartOperation;
  /** Timestamp Unix (ms) do momento do dispatch */
  timestamp: number;
  /** Estado completo do carrinho retornado pelo Shopify */
  cart: ShopifyCart;
}

// ─── Estado compartilhado via window ─────────────────────────────────────────
// Compartilhado entre múltiplas instâncias do módulo.
// __cartBusFp / __cartBusTs: fingerprint + timestamp do último dispatch (distinctUntilChanged).
//
// H5: interface única tipando TODAS as props __cartBus* que vivem no window.
// Estende Window para preservar tipagem de fetch/XMLHttpRequest/etc quando
// castamos. Usar `const w = window as CartBusWindow` em vez de `(window as any)`.

// Estende Window só com o que é EXCLUSIVO do bus. Helpers públicos
// (dispatchCartEvent, fetchCartAndDispatch) são declarados em index.d.ts como
// parte de Window direto — não redeclarar aqui para evitar conflito de opcional.
interface CartBusWindow extends Window {
  __cartBusFp?: string;
  __cartBusTs?: number;
  __cartBusFetchPatched?: boolean;
  __cartBusXHRPatched?: boolean;
  __cartBusDisableFetchPatch?: boolean;
  routes?: { cart_url?: string };
}

const DISTINCT_MS = 500;

function cartFingerprint(cart: ShopifyCart): string {
  const items = cart.items
    .map((i) => `${i.variant_id}:${i.quantity}`)
    .sort()
    .join(",");
  return `${cart.token}|${items}`;
}

// H2: valida shape mínimo de cart antes de emitir. dispatch() é exposto via
// window.dispatchCartEvent, então qualquer script na página pode chamá-lo.
// Sem essa validação, um terceiro poderia injetar um "cart" com token arbitrário
// e o cart-attribute-tracker persistiria esse token via POST /cart/update.js.
// Também protege cartFingerprint() de cart.items undefined (crash em .map).
function isValidCart(cart: unknown): cart is ShopifyCart {
  if (!cart || typeof cart !== "object") return false;
  const c = cart as Partial<ShopifyCart>;
  return typeof c.token === "string" && c.token.length > 0 && Array.isArray(c.items);
}

// ─── dispatch ────────────────────────────────────────────────────────────────
// Descarta silenciosamente se o fingerprint do carrinho for igual ao último emitido
// dentro de DISTINCT_MS — protege todos os listeners sem que cada um precise
// implementar seu próprio guard.

export function dispatch(detail: Omit<CartChangedDetail, "timestamp"> & { timestamp?: number }): void {
  // H2: rejeita payloads que não são carts válidos. Log em warn para facilitar
  // debug se algum call-site legítimo estiver passando dados malformados.
  if (!isValidCart(detail.cart)) {
    console.warn("[cart-event-bus] dispatch REJEITADO — cart inválido", {
      source: detail.source,
      operation: detail.operation,
      cartType: typeof detail.cart,
    });
    return;
  }

  const w = window as CartBusWindow;
  const fp = cartFingerprint(detail.cart);
  const now = Date.now();
  const lastFp = w.__cartBusFp ?? "";
  const lastTs = w.__cartBusTs ?? 0;

  if (fp === lastFp && now - lastTs < DISTINCT_MS) {
    console.debug(`[cart-event-bus] dispatch DESCARTADO — mesmo fp dentro de ${DISTINCT_MS}ms`, {
      source: detail.source,
      operation: detail.operation,
      fp,
      msSinceLastDispatch: now - lastTs,
    });
    return;
  }

  console.debug("[cart-event-bus] dispatch EMITIDO", {
    source: detail.source,
    operation: detail.operation,
    fp,
    item_count: detail.cart.item_count,
    prevFp: lastFp,
    msSinceLastDispatch: now - lastTs,
  });

  w.__cartBusFp = fp;
  w.__cartBusTs = now;
  _lastInternalDispatchTs = now;

  document.dispatchEvent(
    new CustomEvent<CartChangedDetail>(INSIDER_CART_CHANGED, {
      bubbles: true,
      detail: { ...detail, timestamp: detail.timestamp ?? now },
    }),
  );
}

// ─── fetchCart com cache por in-flight ───────────────────────────────────────
// Não usa TTL fixo. Enquanto um GET /cart.js estiver em flight, reutiliza
// a mesma Promise. Quando resolve, limpa o cache imediatamente.
// Resultado: zero estado stale + zero requests duplicados concorrentes.

const cartUrl = (): string => {
  const routes = (window as CartBusWindow).routes;
  return routes?.cart_url ? `${routes.cart_url}.js` : "/cart.js";
};

let _inFlightCart: Promise<ShopifyCart> | null = null;

function fetchCart(): Promise<ShopifyCart> {
  if (_inFlightCart) return _inFlightCart;
  _inFlightCart = fetch(cartUrl(), {
    credentials: "same-origin",
    headers: { "Cache-Control": "no-cache" },
  })
    .then((r) => r.json() as Promise<ShopifyCart>)
    .finally(() => {
      _inFlightCart = null;
    });
  return _inFlightCart;
}

// ─── fetchAndDispatch ─────────────────────────────────────────────────────────
// In-flight dedup: se já há um fetchAndDispatch em curso, o segundo é descartado.
// Isso previne dois PerformanceObserver callbacks para o mesmo URL causarem
// dois fetches independentes (o in-flight do fetchCart() pode já ter sido limpo
// entre os dois callbacks).

let _inFlightDispatch: Promise<void> | null = null;

export function fetchAndDispatch(source: CartChangedSource, operation: CartOperation): Promise<void> {
  if (_inFlightDispatch) {
    console.debug("[cart-event-bus] fetchAndDispatch SKIP — já há um em andamento", { source, operation });
    return _inFlightDispatch;
  }
  _inFlightDispatch = (async () => {
    try {
      const cart = await fetchCart();
      dispatch({ source, operation, cart });
    } catch (err) {
      document.dispatchEvent(
        new CustomEvent(INSIDER_CART_ERROR, {
          bubbles: true,
          detail: { source, error: err },
        }),
      );
    } finally {
      _inFlightDispatch = null;
    }
  })();
  return _inFlightDispatch;
}

// ─── silentCartUpdate ─────────────────────────────────────────────────────────
// Para listeners que precisam fazer POST /cart/update.js como side-effect.
// Atualiza _lastInternalDispatchTs antes do fetch para que o PerformanceObserver
// não interprete esse request como uma mutação externa e gere novo dispatch.
// Sem isso: listener → POST /cart/update → PerformanceObserver → fetchAndDispatch
//           → insider:cart:changed → listener → POST /cart/update → loop infinito.
//
// Uso: await silentCartUpdate({ attributes: { _cartId: token } })
//      await silentCartUpdate({ note: 'texto da nota' })

export async function silentCartUpdate(body: Record<string, unknown>): Promise<void> {
  _lastInternalDispatchTs = Date.now(); // suprime PerformanceObserver antes do fetch
  const response = await fetch("/cart/update.js", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).finally(() => {
    _lastInternalDispatchTs = Date.now(); // atualiza no momento em que o PerformanceObserver recebe a entrada
  });
  if (!response.ok) {
    throw new Error(`[cart-event-bus] Cart update failed: ${response.status}`);
  }
}

// ─── Helpers globais para arquivos .js legados ────────────────────────────────
// Não expõe o bus em window. Expõe apenas dois helpers de uma linha
// para que os arquivos .js legados não precisem de import/export.
// Uso: dispatchCartEvent({ source, operation, cart })
//      fetchCartAndDispatch(source, operation)

// H5: atribuição dos helpers globais. Os tipos públicos são declarados em
// index.d.ts (consumido pelos outros .ts/.js via src/types). Aqui precisamos
// de um único cast para contornar o fato de que o pipeline `typescriptModule`
// não inclui index.d.ts — isolamos em um tipo local explícito em vez de `any`.
type CartBusGlobalHelpers = {
  dispatchCartEvent: (detail: Omit<CartChangedDetail, "timestamp">) => void;
  fetchCartAndDispatch: (source: CartChangedSource, operation: CartOperation) => Promise<void>;
};
const wGlobals = window as unknown as CartBusGlobalHelpers;
wGlobals.dispatchCartEvent = (detail) => dispatch(detail);
wGlobals.fetchCartAndDispatch = (source, operation) => fetchAndDispatch(source, operation);

// ─── Detecção de apps externos — 2 camadas em paralelo ───────────────────────
//
// Apps de terceiros (freegifts, Stamped, bundles) fazem POST /cart/*.js direto,
// sem passar pelo bus. Precisamos detectar isso para que o cart-attribute-tracker
// e outros listeners reajam.
//
// CAMADA 1 (preferencial): fetch/XHR monkey-patch — captura o body da RESPOSTA
//   imediatamente, sem janela de supressão, sem GET extra de /cart.js.
//   Latência ~0ms. Pega 99% dos casos (apps modernos usam fetch).
//
// CAMADA 2 (fallback): PerformanceObserver — já existia, preservado INTACTO.
//   Latência ~50-200ms + janela de supressão de 2s. Pega casos onde o patch
//   não conseguiu instalar (erro em script anterior) ou onde o app usa
//   sendBeacon/Image requests (raro mas possível).
//
// As duas camadas rodam em paralelo. Se ambas detectarem o mesmo POST externo,
// o distinctUntilChanged do dispatch() (fingerprint cart + DISTINCT_MS=500ms)
// descarta o duplicado automaticamente. Zero evento duplicado por design.

const CART_MUTATION_RE = /\/cart\/(add|change|update)(?:\.js|\.json)?(\?.*)?$/;

// Estado compartilhado entre as duas camadas + silentCartUpdate.
// _lastInternalDispatchTs: atualizado por dispatch() e silentCartUpdate.
//   Usado pelo Observer para ignorar POSTs que são follow-ups de ações internas.
//   (Camada 1 não precisa disso — ela lê o body direto, dispatch() dedup por fingerprint.)
let _lastInternalDispatchTs = 0;
let _lastObserverDispatchTs = 0;

const DEDUP_MS = 2000;
const OBSERVER_COOLDOWN_MS = 3000;

// ─── CAMADA 1: fetch/XHR monkey-patch ────────────────────────────────────────
//
// Princípios de segurança (NÃO alterar sem reler todos os comentários):
//
// A) NUNCA quebrar a request original.
//    Se QUALQUER coisa no sniffing falhar (sync ou async), a request real
//    precisa retornar normalmente. Usuários não podem ver cart quebrado por
//    causa do nosso telemetry.
//
// B) NUNCA quebrar a cadeia de patches de outros scripts.
//    Salvamos o `fetch` que estava lá ANTES de nós (pode já ter sido patched
//    por Statsig, Insider SDK, etc.) e chamamos ELE — nunca um fetch "nativo".
//    Quem vier depois de nós vai nos englobar da mesma forma.
//
// C) Response body é um stream de leitura única.
//    Se consumirmos o body, o código original não consegue mais ler.
//    Sempre usar `response.clone()` antes de `.json()`.
//
// D) Idempotência.
//    Se o script for carregado 2x (hot reload, double-include acidental),
//    o guard `__cartBusFetchPatched` impede duplicação da cadeia.
//
// E) Kill switch em produção.
//    `window.__cartBusDisableFetchPatch = true` no console + F5 desliga a camada 1
//    sem deploy. A camada 2 (Observer) continua ativa independente.
//
// F) No pior caso, comportamento = main atual.
//    Se o patch falhar em instalar, o Observer que sempre existiu continua
//    funcionando. Não introduzimos regressão em nenhum cenário.

// H5: CartBusWindow já declarado no topo do arquivo (única fonte de verdade).
// Esses patches usam a mesma interface.

function installFetchPatch(): void {
  const w = window as CartBusWindow;

  // Kill switch: permite desligar via console em produção sem deploy.
  if (w.__cartBusDisableFetchPatch === true) {
    console.debug("[cart-event-bus] fetch patch DESLIGADO via __cartBusDisableFetchPatch");
    return;
  }

  // Guard idempotente: se já patchamos, não empilha de novo.
  if (w.__cartBusFetchPatched === true) {
    console.debug("[cart-event-bus] fetch patch já instalado — skip");
    return;
  }

  // typeof guard: ambientes sem fetch (SSR, workers antigos) — não crítico, Observer cobre.
  if (typeof window.fetch !== "function") {
    console.debug("[cart-event-bus] window.fetch indisponível — fetch patch skip");
    return;
  }

  try {
    // IMPORTANTE: __previousFetch pode JÁ SER um fetch patched por outro script.
    // Sempre chamamos ELE, nunca window.fetch original/nativo. Isso preserva
    // a cadeia de patches existente (Statsig, Shopify SDK, etc.).
    const __previousFetch = window.fetch.bind(window);

    // Helper: extrai URL string de qualquer input aceito por fetch (string | URL | Request).
    // try/catch porque input pode ser malformado — se não conseguirmos extrair,
    // o sniffing simplesmente não roda pra essa request (a request original segue normal).
    const extractUrl = (input: RequestInfo | URL): string => {
      try {
        if (typeof input === "string") return input;
        if (input instanceof URL) return input.toString();
        if (input instanceof Request) return input.url;
        return "";
      } catch {
        return "";
      }
    };

    // Helper: identifica se é uma mutação de cart que nos interessa.
    // Só POST em /cart/(add|change|update) — GET /cart.js não gera dispatch.
    const isCartMutation = (input: RequestInfo | URL, init?: RequestInit): boolean => {
      try {
        const url = extractUrl(input);
        if (!url || !CART_MUTATION_RE.test(url)) return false;

        // Descobre o método: pode vir em init.method OU (se input for Request) em input.method.
        const method = init?.method?.toUpperCase() ?? (input instanceof Request ? input.method.toUpperCase() : "GET");
        return method === "POST";
      } catch {
        return false;
      }
    };

    window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // Chama o fetch anterior PRIMEIRO. Se ele lançar (sync ou async),
      // deixamos o erro propagar exatamente como propagaria sem o patch.
      // NÃO engolimos erros de rede — isso mascararia bugs reais pro código chamador.
      const response = await __previousFetch(input, init);

      // A partir daqui é só sniffing. Tudo envolvido em try/catch aninhado.
      // Qualquer erro aqui NÃO pode afetar o caller — ele já tem a `response`.
      try {
        if (!isCartMutation(input, init)) return response;

        // response.clone() cria um Response independente com seu próprio body stream.
        // Consumir o clone não afeta o body do response original que vai pro caller.
        // clone() pode lançar se o body já foi consumido antes por outro patch —
        // por isso está dentro do try/catch.
        const cloned = response.clone();

        // Disparamos async, sem await. O caller não espera nosso sniffing.
        // .catch(() => {}) silencia JSON inválido, body vazio, corrida de leitura, etc.
        cloned
          .json()
          .then((cart: unknown) => {
            // Validação mínima: precisa ser objeto com `token` (shape mínimo do cart).
            // Sem isso, dispatch() vai quebrar em cartFingerprint(). Descarta silenciosamente
            // se a resposta não for um cart (algum app pode estar postando em endpoint
            // com mesmo path mas retornando formato diferente).
            if (
              cart &&
              typeof cart === "object" &&
              "token" in cart &&
              "items" in cart &&
              Array.isArray((cart as { items: unknown }).items)
            ) {
              // dispatch() já tem distinctUntilChanged por fingerprint — se o Observer
              // também detectar essa mesma mutação dentro de 500ms, o 2º é descartado.
              dispatch({
                source: "external",
                operation: "update",
                cart: cart as ShopifyCart,
              });
            }
          })
          .catch(() => {
            // body consumido, JSON inválido, resposta não-JSON — todos caem aqui.
            // Observer continua como fallback, então não precisamos agir.
          });
      } catch {
        // clone() falhou ou extractUrl lançou — silencia. Request original já retornou.
      }

      return response;
    };

    // Marca no window DEPOIS de substituir o fetch, para que um double-load
    // que acontecer ENTRE a linha acima e esta não passe pelo guard.
    w.__cartBusFetchPatched = true;
    console.debug("[cart-event-bus] fetch patch INSTALADO");
  } catch (err) {
    // Falha catastrófica ao instalar (raríssimo — só se window.fetch for non-configurable,
    // o que não acontece em browsers normais). Observer cobre.
    console.warn("[cart-event-bus] fetch patch FALHOU ao instalar, usando Observer como fallback:", err);
  }
}

function installXHRPatch(): void {
  const w = window as CartBusWindow;

  if (w.__cartBusDisableFetchPatch === true) return;
  if (w.__cartBusXHRPatched === true) return;
  if (typeof XMLHttpRequest === "undefined") return;

  try {
    // Preserva originais do prototype — se alguém já patchou, pegamos o patched.
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    // Guardamos URL + método em cada instância via WeakMap
    // (não poluimos o objeto XHR com propriedades próprias que outros patches
    // poderiam sobrescrever).
    const xhrMeta = new WeakMap<XMLHttpRequest, { url: string; method: string }>();

    XMLHttpRequest.prototype.open = function patchedOpen(
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ): void {
      try {
        xhrMeta.set(this, {
          method: String(method).toUpperCase(),
          url: typeof url === "string" ? url : url.toString(),
        });
      } catch {
        // se WeakMap.set falhar por algum motivo bizarro, apenas não rastreamos
      }
      return originalOpen.apply(this, [method, url, ...rest] as Parameters<typeof originalOpen>);
    };

    XMLHttpRequest.prototype.send = function patchedSend(this: XMLHttpRequest, ...args: unknown[]): void {
      try {
        const meta = xhrMeta.get(this);
        if (meta && meta.method === "POST" && CART_MUTATION_RE.test(meta.url)) {
          // Hook em loadend — dispara ao final, bem ou mal sucedido.
          // Listener anônimo, não removível por outros — mas XHR é descartado após
          // a request, então não há leak.
          this.addEventListener("loadend", () => {
            try {
              // status 0 = abortado/CORS falhou, não temos cart válido
              if (this.status < 200 || this.status >= 300) return;
              const body = this.responseText;
              if (!body) return;
              const cart = JSON.parse(body);
              if (cart && typeof cart === "object" && "token" in cart && "items" in cart && Array.isArray(cart.items)) {
                dispatch({ source: "external", operation: "update", cart });
              }
            } catch {
              // JSON inválido, responseText não acessível (responseType != '' | 'text')
            }
          });
        }
      } catch {
        // sniffing nunca bloqueia o send
      }
      return originalSend.apply(this, args as Parameters<typeof originalSend>);
    };

    w.__cartBusXHRPatched = true;
    console.debug("[cart-event-bus] XHR patch INSTALADO");
  } catch (err) {
    console.warn("[cart-event-bus] XHR patch FALHOU ao instalar, usando Observer como fallback:", err);
  }
}

// Instala ambos os patches. Cada um tem guard próprio — falha independente.
installFetchPatch();
installXHRPatch();

// ─── CAMADA 2: PerformanceObserver (fallback, INTACTO) ───────────────────────
// Preservado exatamente como estava. Cobre:
// - sendBeacon, Image beacons (raro para cart mas teoricamente possível)
// - Caso catastrófico onde AMBOS fetch patch E XHR patch falharam ao instalar
// - Requests que aconteceram ANTES do patch ser instalado (buffered: true)
//
// Duas camadas de deduplicação (não confundir com a dedup por fingerprint do dispatch()):
// 1. DEDUP_MS: ignora se um dispatch interno ocorreu nos últimos 2s.
// 2. OBSERVER_COOLDOWN_MS: após disparar um fetchAndDispatch, ignora novas entradas
//    por 3s (previne múltiplos callbacks buffered+realtime de causarem dispatches em sequência).
//
// IMPORTANTE: dispatches da CAMADA 1 também atualizam _lastInternalDispatchTs
// (via dispatch() → linha que seta `_lastInternalDispatchTs = now`), então o
// Observer naturalmente suprime entradas já capturadas pelo fetch patch.

if (typeof PerformanceObserver !== "undefined") {
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!CART_MUTATION_RE.test(entry.name)) continue;
        const now = Date.now();
        const msSinceInternal = now - _lastInternalDispatchTs;
        const msSinceObserver = now - _lastObserverDispatchTs;

        if (msSinceInternal <= DEDUP_MS) {
          console.debug("[cart-event-bus] PerformanceObserver SUPRIMIDO — dispatch interno recente", {
            url: entry.name,
            msSinceInternal,
          });
          break;
        }

        if (msSinceObserver <= OBSERVER_COOLDOWN_MS) {
          console.debug("[cart-event-bus] PerformanceObserver SUPRIMIDO — cooldown ativo", {
            url: entry.name,
            msSinceObserver,
          });
          break;
        }

        _lastObserverDispatchTs = now;
        console.debug("[cart-event-bus] PerformanceObserver → fetchAndDispatch external", {
          url: entry.name,
          msSinceInternal,
        });
        fetchAndDispatch("external", "update");
        break;
      }
    }).observe({ type: "resource", buffered: true });
  } catch {
    // PerformanceObserver não suportado — silencioso, não é crítico
  }
}
